import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SocialAccountTypeDialog } from '@/components/auth/SocialAccountTypeDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { socialAuthPlugin } from '@/plugins/social-auth/plugin';
import type {
  AuthError,
  AuthResponse,
  AuthResponseData,
  AuthResponseMeta,
  AuthScope,
  OAuthProvider,
  SocialAuthIntent,
} from '@/types/auth';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth.service';
import { customerSubscriptionsService } from '@/services/customer/subscriptions.service';
import type { CustomerSubscriptionResource } from '@/types/customer-subscriptions';
import { resolveSigninRedirectUrl } from '@/lib/auth/signin-redirect';

type Status = 'pending' | 'awaiting-onboarding' | 'success' | 'error';

function resolveAuthData(payload: AuthResponse | AuthResponse['data'] | undefined | null): AuthResponse['data'] | null {
  if (!payload) {
    return null;
  }

  if ('data' in payload && payload.data && typeof payload.data === 'object') {
    return payload.data as AuthResponse['data'];
  }

  if (
    'user' in payload &&
    'provider' in payload &&
    'token' in payload &&
    payload.user &&
    payload.provider
  ) {
    return payload as AuthResponse['data'];
  }

  return null;
}

function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unknown authentication error.';
  }

  if (
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as { data?: AuthError; message?: string };
    const message = response.data?.message ?? response.data?.error ?? response.message;
    if (message) {
      return message;
    }
  }

  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }

  return 'Unable to complete social sign-in. Please try again.';
}

function isOAuthDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const record = error as { response?: { status?: number; data?: unknown }; status?: number };
  const status = record.response?.status ?? record.status;
  if (status !== 422) {
    return false;
  }

  const data = record.response?.data;
  if (!data || typeof data !== 'object') {
    return false;
  }

  const payload = data as Record<string, unknown>;
  return typeof payload.error === 'string' || typeof payload.error_description === 'string';
}

function resolveSubscriptionEndDate(
  subscriptions: CustomerSubscriptionResource[],
  language: string,
): string | null {
  const formatter = new Intl.DateTimeFormat(language, { dateStyle: 'medium' });
  const active = subscriptions.find((subscription) =>
    ['active', 'trialing'].includes(subscription.attributes.status),
  );

  const candidate = active?.attributes.endsAt ?? active?.attributes.currentPeriodEnd;
  if (!candidate) {
    return null;
  }

  const parsed = new Date(candidate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return formatter.format(parsed);
}

export default function OAuthCallback() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: establishProviderSession } = useAuth();
  const customerAuth = useCustomerAuth();

  const [status, setStatus] = useState<Status>('pending');
  const [error, setError] = useState<string | null>(null);
  const [resolvedScope, setResolvedScope] = useState<AuthScope>('provider');
  const [resolvedProvider, setResolvedProvider] = useState<OAuthProvider | undefined>(undefined);
  const [onboardingState, setOnboardingState] = useState<{
    scope: AuthScope;
    provider: OAuthProvider | undefined;
    onboardingUserId: string;
    data: AuthResponseData;
    meta?: AuthResponseMeta;
  } | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  const hasProcessedRef = useRef(false);
  const isActiveRef = useRef(true);
  const lastScopeRef = useRef<AuthScope | null>(null);
  const lastProviderRef = useRef<OAuthProvider | null>(null);
  const isExchangeInFlightRef = useRef(false);

  const redirectAfterSuccess = useCallback(
    (scope: AuthScope, meta?: AuthResponseMeta) => {
      if (scope === 'customer') {
        const redirectUrl = resolveSigninRedirectUrl({ meta } as AuthResponseData);
        if (redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }
      }

      const target = scope === 'provider' ? '/provider/dashboard' : '/customer';
      console.info('[oauth] navigating to dashboard', { scope, target });
      navigate(target, { replace: true });
    },
    [navigate],
  );

  const completeLogin = useCallback(
    async (scope: AuthScope, data: AuthResponse['data']) => {
      if (!isActiveRef.current) {
        return;
      }

      console.info('[oauth] handleSuccess', { scope, user: data.user?.email, hasToken: Boolean(data.token) });

      if (scope === 'provider') {
        await establishProviderSession(data.user, data.provider, data.token);
      } else {
        await customerAuth.hydrateFromResponse(data);
      }
    },
    [customerAuth, establishProviderSession],
  );

  const processSuccessfulAuth = useCallback(
    async (
      scope: AuthScope,
      provider: OAuthProvider | undefined,
      data: AuthResponseData,
      meta?: AuthResponseMeta,
    ) => {
      await completeLogin(scope, data);

      if (meta?.is_new_user) {
        const onboardingUserId = meta.onboarding_user_id ?? data.user.id;
        setOnboardingState({
          scope,
          provider,
          onboardingUserId: String(onboardingUserId),
          data,
          meta,
        });
        setStatus('awaiting-onboarding');
        setOnboardingError(null);
        setIsRoleDialogOpen(true);
        return;
      }

      setStatus('success');
      redirectAfterSuccess(scope, data.meta ?? meta);
    },
    [completeLogin, redirectAfterSuccess],
  );

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasProcessedRef.current || !isActiveRef.current || isExchangeInFlightRef.current) {
      return;
    }

    const completed = socialAuthPlugin.consumeCompletedState();
    if (completed) {
      hasProcessedRef.current = true;
      setResolvedScope(completed.scope);
      setResolvedProvider(completed.provider);
      const completedData = resolveAuthData(completed.response.data ?? completed.response);
      const completedMeta =
        (completed.response.data?.meta ??
          (completed.response.meta as AuthResponseMeta | undefined)) ??
        undefined;
      const completedToken = completedData?.token ?? authService.getAuthToken(completed.scope);
      if (!completedData?.user || !completedData?.provider || !completedToken) {
        setError(t('auth.oauthMissingCode', 'Missing authorization code. Please try again.'));
        setStatus('error');
        return;
      }
      const normalizedCompleted: AuthResponseData = { ...completedData, token: completedToken };
      void processSuccessfulAuth(completed.scope, completed.provider, normalizedCompleted, completedMeta);
      return;
    }

    const run = async () => {
      const code = searchParams.get('code');
      const stateParam = searchParams.get('state');
      const oauthError = searchParams.get('error');
      const oauthErrorDescription =
        searchParams.get('error_description') ?? searchParams.get('message');

      const pending = socialAuthPlugin.getPendingState();

      const scopeParam = searchParams.get('scope');
      const providerParam = searchParams.get('provider');

      const scope: AuthScope | undefined =
        pending?.scope ??
        lastScopeRef.current ??
        (scopeParam === 'provider' || scopeParam === 'customer' ? (scopeParam as AuthScope) : undefined);

      const provider: OAuthProvider | undefined =
        pending?.provider ??
        lastProviderRef.current ??
        (providerParam === 'google' ||
        providerParam === 'github' ||
        providerParam === 'apple' ||
        providerParam === 'azure'
          ? (providerParam as OAuthProvider)
          : undefined);

      const idToken = searchParams.get('id_token') ?? undefined;
      const userPayload = searchParams.get('user');
      let parsedUser: unknown = undefined;
      if (userPayload) {
        try {
          parsedUser = JSON.parse(userPayload);
        } catch {
          parsedUser = userPayload;
        }
      }

      if (scope) {
        lastScopeRef.current = scope;
        setResolvedScope(scope);
      }
      if (provider) {
        lastProviderRef.current = provider;
        setResolvedProvider(provider);
      }

      if (oauthError) {
        if (oauthError === 'access_denied') {
          setError(t('auth.oauthDenied', 'Authorization denied. Please try again.'));
        } else {
          setError(oauthErrorDescription ?? oauthError);
        }
        setStatus('error');
        return;
      }

      if (!code) {
        socialAuthPlugin.clearPendingState();
        setError(t('auth.oauthMissingCode', 'Missing authorization code. Please try again.'));
        setStatus('error');
        return;
      }

      if (!scope || !provider) {
        socialAuthPlugin.clearPendingState();
        setError(t('auth.oauthUnknownContext', 'We could not determine which portal to sign in.'));
        setStatus('error');
        return;
      }

       
      console.info('[oauth] starting exchange', {
        scope,
        provider,
        stateParam,
        hasCode: Boolean(code),
        pending,
      });

      const response = await socialAuthPlugin.complete({
        scope,
        provider,
        code,
        state: stateParam ?? undefined,
        idToken,
        user: parsedUser,
      });

      const payload = resolveAuthData(response.data ?? response);
      const responseMeta = (response.data?.meta ?? (response.meta as AuthResponseMeta | undefined)) ?? undefined;
      const payloadMeta = payload?.meta ?? responseMeta;
      
      console.info('[oauth] exchange response', {
        scope,
        provider,
        tokenInPayload: payload?.token,
        tokenInStorage: authService.getAuthToken(scope),
      });
      const tokenFromPayload = payload?.token ?? authService.getAuthToken(scope);
      if (!payload?.user || !payload?.provider || !tokenFromPayload) {
        throw new Error('Missing authentication token in OAuth response.');
      }

      hasProcessedRef.current = true;
      console.info('[oauth] exchange success', { scope, provider });
      const normalizedPayload: AuthResponseData = { ...payload, token: tokenFromPayload };
      await processSuccessfulAuth(scope, provider, normalizedPayload, payloadMeta);
    };

    isExchangeInFlightRef.current = true;

    void run()
      .catch((err) => {
        socialAuthPlugin.clearPendingState();
        console.error('[oauth] exchange failed', err);
        const fallbackScope = lastScopeRef.current ?? resolvedScope;
        const token = authService.getAuthToken(fallbackScope ?? 'provider');
        if (token) {
          console.info('[oauth] token already stored after error; proceeding with redirect', {
            scope: fallbackScope,
          });
          setStatus('success');
          redirectAfterSuccess(fallbackScope ?? 'provider');
          return;
        }

        if (isOAuthDenied(err)) {
          setError(t('auth.oauthDenied', 'Authorization denied. Please try again.'));
        } else {
          setError(extractErrorMessage(err));
        }
        setStatus('error');
      })
      .finally(() => {
        isExchangeInFlightRef.current = false;
      });
  }, [processSuccessfulAuth, redirectAfterSuccess, resolvedScope, searchParams, t]);

  const handleRoleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && onboardingState) {
        return;
      }
      setIsRoleDialogOpen(open);
    },
    [onboardingState],
  );

  const handleOnboardingConfirm = useCallback(
    async (intent: Required<Pick<SocialAuthIntent, 'desiredRole'>> & SocialAuthIntent) => {
      const currentState = onboardingState;
      if (!currentState) {
        return;
      }

      setIsCompletingOnboarding(true);
      setOnboardingError(null);

      try {
        const response = await authService.completeSocialOnboarding(
          currentState.scope,
          currentState.onboardingUserId,
          intent,
          currentState.provider,
        );

        if (response?.data) {
          await completeLogin(currentState.scope, response.data);
          if (currentState.scope === 'customer') {
            await customerAuth.checkAuth();
          }
        }

        const responseMeta =
          response?.meta ??
          (response?.data?.meta as AuthResponseMeta | undefined) ??
          undefined;

        if (currentState.scope === 'customer' && intent.desiredRole === 'team_owner') {
          const cancellationFlagRaw = responseMeta?.subscription_canceled_at_period_end;
          const cancellationFlag =
            typeof cancellationFlagRaw === 'boolean'
              ? cancellationFlagRaw
              : (responseMeta as { subscriptionCanceledAtPeriodEnd?: boolean } | undefined)
                  ?.subscriptionCanceledAtPeriodEnd;

          if (cancellationFlag) {
            try {
              const subscriptionResponse = await customerSubscriptionsService.list(['active', 'trialing']);
              const endDate = resolveSubscriptionEndDate(
                subscriptionResponse.data ?? [],
                i18n.language ?? i18n.resolvedLanguage ?? 'en',
              );
              const message = endDate
                ? t('customers.teams.create.subscriptionCanceledWithDate', { date: endDate })
                : t('customers.teams.create.subscriptionCanceled');
              toast(message);
            } catch (noticeError) {
              console.warn('[oauth] unable to load subscription notice', noticeError);
              toast(t('customers.teams.create.subscriptionCanceled'));
            }
          }

          setOnboardingState(null);
          setIsRoleDialogOpen(false);
          setStatus('success');
          navigate('/customer/register?planType=teams', { replace: true });
          return;
        }

        setOnboardingState(null);
        setIsRoleDialogOpen(false);
        setStatus('success');
      redirectAfterSuccess(currentState.scope, responseMeta);
      } catch (err) {
        console.error('[oauth] onboarding completion failed', err);
        const message = extractErrorMessage(err);
        setOnboardingError(message);
      } finally {
        setIsCompletingOnboarding(false);
      }
    },
    [completeLogin, customerAuth, i18n.language, i18n.resolvedLanguage, navigate, onboardingState, redirectAfterSuccess, t],
  );

  const backToLoginPath = resolvedScope === 'customer' ? '/customer/login' : '/provider/login';
  const fallbackLabel =
    resolvedScope === 'customer'
      ? t('customer.portal.login.title')
      : t('auth.providerSignIn');

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>
            {status === 'error'
              ? t('auth.oauthErrorTitle', 'Sign-in failed')
              : t('auth.oauthSigningIn', 'Signing you in')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {status === 'pending' ? (
            <>
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {resolvedProvider
                  ? t('auth.oauthPendingWithProvider', 'Completing sign-in with {{provider}}...', {
                      provider: resolvedProvider,
                    })
                  : t('auth.oauthPending', 'Completing sign-in...')}
              </p>
            </>
          ) : null}

          {status === 'awaiting-onboarding' ? (
            <p className="text-sm text-muted-foreground">
              {t('auth.oauthAwaitingOnboarding', 'Almost done — choose how you plan to use Florentin to finish setup.')}
            </p>
          ) : null}

          {status === 'awaiting-onboarding' && onboardingError ? (
            <p className="text-sm text-destructive">{onboardingError}</p>
          ) : null}

          {status === 'error' && error ? (
            <>
              <p className="text-sm text-destructive">{error}</p>
              <Button
                className="w-full"
                onClick={() => {
                  socialAuthPlugin.clearPendingState();
                  navigate(backToLoginPath, { replace: true });
                }}
              >
                {t('auth.oauthReturnToLogin', 'Back to {{context}} login', {
                  context: fallbackLabel,
                })}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
      <SocialAccountTypeDialog
        open={isRoleDialogOpen}
        onOpenChange={handleRoleDialogOpenChange}
        onConfirm={handleOnboardingConfirm}
        defaultTeamSeats={10}
        isSubmitting={isCompletingOnboarding}
      />
    </div>
  );
}
